<template>
  <div>
    <div class="relative">
      <div id="results-container" class="card" v-show="hasSearch">
        <div class="card-header">
          {{ $t("your_results") }}
        </div>
        <div class="card-content">
          <ul v-if="hasSearch" class="list-group">
            <li
              class="list-group-item"
              v-for="(i, index) in markers"
              :key="index"
              @click="toggleInfoWindow(i, index)"
            >
              <div>
                <strong>{{ i.data.name }}</strong>
              </div>
              <div>{{ i.data.address }}</div>
              <div>
                {{ i.data.zipcode }} -
                {{ i.data.city }}
              </div>
              <div>{{ i.data.country }}</div>
              <div v-if="i.data.phone">
                <svg
                  aria-hidden="true"
                  focusable="false"
                  data-prefix="far"
                  data-icon="phone"
                  class="svg-inline--fa fa-phone fa-w-16"
                  role="img"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 512 512"
                >
                  <path
                    fill="currentColor"
                    d="M476.5 22.9L382.3 1.2c-21.6-5-43.6 6.2-52.3 26.6l-43.5 101.5c-8 18.6-2.6 40.6 13.1 53.4l40 32.7C311 267.8 267.8 311 215.4 339.5l-32.7-40c-12.8-15.7-34.8-21.1-53.4-13.1L27.7 329.9c-20.4 8.7-31.5 30.7-26.6 52.3l21.7 94.2c4.8 20.9 23.2 35.5 44.6 35.5C312.3 512 512 313.7 512 67.5c0-21.4-14.6-39.8-35.5-44.6zM69.3 464l-20.9-90.7 98.2-42.1 55.7 68.1c98.8-46.4 150.6-98 197-197l-68.1-55.7 42.1-98.2L464 69.3C463 286.9 286.9 463 69.3 464z"
                  ></path>
                </svg>
                <a :href="'tel:' + i.data.phone">{{ i.data.phone }}</a>
              </div>
              <div v-if="i.data.email">
                <svg
                  aria-hidden="true"
                  focusable="false"
                  data-prefix="far"
                  data-icon="envelope"
                  class="svg-inline--fa fa-envelope fa-w-16"
                  role="img"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 512 512"
                >
                  <path
                    fill="currentColor"
                    d="M464 64H48C21.49 64 0 85.49 0 112v288c0 26.51 21.49 48 48 48h416c26.51 0 48-21.49 48-48V112c0-26.51-21.49-48-48-48zm0 48v40.805c-22.422 18.259-58.168 46.651-134.587 106.49-16.841 13.247-50.201 45.072-73.413 44.701-23.208.375-56.579-31.459-73.413-44.701C106.18 199.465 70.425 171.067 48 152.805V112h416zM48 400V214.398c22.914 18.251 55.409 43.862 104.938 82.646 21.857 17.205 60.134 55.186 103.062 54.955 42.717.231 80.509-37.199 103.053-54.947 49.528-38.783 82.032-64.401 104.947-82.653V400H48z"
                  ></path>
                </svg>
                <a :href="'mailto:' + i.data.email">{{ i.data.email }}</a>
              </div>
              <div v-if="i.data.website">
                <svg
                  aria-hidden="true"
                  focusable="false"
                  data-prefix="far"
                  data-icon="browser"
                  class="svg-inline--fa fa-browser fa-w-16"
                  role="img"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 512 512"
                >
                  <path
                    fill="currentColor"
                    d="M464 32H48C21.5 32 0 53.5 0 80v352c0 26.5 21.5 48 48 48h416c26.5 0 48-21.5 48-48V80c0-26.5-21.5-48-48-48zM48 92c0-6.6 5.4-12 12-12h40c6.6 0 12 5.4 12 12v24c0 6.6-5.4 12-12 12H60c-6.6 0-12-5.4-12-12V92zm416 334c0 3.3-2.7 6-6 6H54c-3.3 0-6-2.7-6-6V168h416v258zm0-310c0 6.6-5.4 12-12 12H172c-6.6 0-12-5.4-12-12V92c0-6.6 5.4-12 12-12h280c6.6 0 12 5.4 12 12v24z"
                  ></path>
                </svg>
                <a
                  :href="'//' + formatWebsiteUrl(i.data.website)"
                  target="_blank"
                  >{{ formatWebsiteUrl(i.data.website) }}</a
                >
              </div>
              <div
                v-if="showBrands && getBrands(i.data)"
                v-html="getBrands(i.data)"
                class="mb-5 mt-5"
              ></div>
              <div v-if="i.data.distance != null">
                <small class="text-muted">
                  {{ i.data.distance.toFixed(2) }}
                  km
                </small>
              </div>
            </li>
          </ul>
        </div>
      </div>
      <gmap-map
        id="resellers-map"
        :center="currentLocation"
        :zoom="zoom"
        :options="options"
      >
        <gmap-cluster :zoom-on-click="true">
          <gmap-marker
            :key="index"
            v-for="(m, index) in markers"
            :position="m.position"
            @click="toggleInfoWindow(m, index)"
          >
          </gmap-marker>
          <gmap-info-window
            :options="infoWindowOptions"
            :position="infoWindowWindowPosition"
            :opened="infoWindowOpen"
            @closeclick="infoWindowOpen = false"
          >
            <div v-html="infoWindowContent"></div>
          </gmap-info-window>
        </gmap-cluster>
      </gmap-map>
      <div id="search-container">
        <form action="#" onsubmit="event.preventDefault();">
          <div class="form-group">
            <div class="input-group">
              <gmap-autocomplete
                @place_changed="fetchNearestResellers"
                :placeholder="$t('enter_address')"
                class="form-control"
              >
              </gmap-autocomplete>
              <div class="input-group-append">
                <button
                  type="button"
                  class="input-group-text"
                  @click="geolocate"
                >
                  <svg
                    aria-hidden="true"
                    focusable="false"
                    data-prefix="fal"
                    heigth="16"
                    width="16"
                    data-icon="compass"
                    class="svg-inline--fa fa-compass fa-w-16"
                    role="img"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 496 512"
                  >
                    <path
                      fill="currentColor"
                      d="M264.97 272.97c9.38-9.37 9.38-24.57 0-33.94-9.37-9.37-24.57-9.37-33.94 0-9.38 9.37-9.38 24.57 0 33.94 9.37 9.37 24.57 9.37 33.94 0zM351.44 125c-2.26 0-4.51.37-6.71 1.16l-154.9 55.85c-7.49 2.7-13.1 8.31-15.8 15.8l-55.85 154.91c-5.65 15.67 10.33 34.27 26.4 34.27 2.26 0 4.51-.37 6.71-1.16l154.9-55.85c7.49-2.7 13.1-8.31 15.8-15.8l55.85-154.9c5.64-15.67-10.33-34.28-26.4-34.28zm-58.65 175.79l-140.1 50.51 50.51-140.11 140.11-50.51-50.52 140.11zM248 8C111.03 8 0 119.03 0 256s111.03 248 248 248 248-111.03 248-248S384.97 8 248 8zm0 464c-119.1 0-216-96.9-216-216S128.9 40 248 40s216 96.9 216 216-96.9 216-216 216z"
                    ></path>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import Api from "../api";
import { gmapApi } from "vue2-google-maps";

export default {
  name: "Map",
  computed: {
    google: gmapApi,
  },
  data() {
    return {
      currentLocation: { lat: 48.833657, lng: 2.352004 },
      zoom: 6,
      markers: [],
      places: [],
      currentPlace: null,
      options: {
        zoomControl: true,
        mapTypeControl: false,
        scaleControl: true,
        streetViewControl: false,
        rotateControl: false,
        fullscreenControl: true,
        disableDefaultUI: false,
      },
      selectedInfoWindow: null,
      infoWindowOptions: {
        pixelOffset: {
          width: 0,
          height: -35,
        },
      },
      infoWindowWindowPosition: null,
      infoWindowOpen: false,
      infoWindowContent: "",
      hasSearch: false,
      products: [],
      brands: [
        "bfix",
        "cobrafastener",
        "felixwood",
        "fiberdeck",
        "fiberon",
        "newtechwood",
        "owlialights",
        "moso",
      ],
      showBrands: new URLSearchParams(window.location.search).get("showBrands"),
      cdnUrl: "https://s3.eu-west-3.amazonaws.com/cdn.felixdistribution.com",
    };
  },
  methods: {
    /**
     * Add markers from data array
     */
    addMarkersFromData(data) {
      if (!data) return;
      data.forEach((i) => {
        this.markers.push({
          data: i,
          position: { lat: parseFloat(i.lat), lng: parseFloat(i.lng) },
        });
      });
    },

    /**
     * Fetch resellers (all pages)
     */
    async fetchAllResellers() {
      try {
        this.markers = [];

        const firstResponse = await Api.get("", {
          params: { page: 1, limit: 250 },
        });
        const result = firstResponse.data;

        if (!result.data) {
          return;
        }

        this.addMarkersFromData(result.data);

        const pagination = result.pagination;
        if (!pagination?.hasMore || pagination.pages <= 1) {
          return;
        }

        const limit = pagination.limit;
        const totalPages = pagination.pages;
        const otherPagePromises = [];

        for (let page = 2; page <= totalPages; page++) {
          otherPagePromises.push(
            Api.get("", { params: { page, limit } }).then((res) => res.data),
          );
        }

        const otherResults = await Promise.all(otherPagePromises);
        otherResults.forEach((pageResult) => {
          this.addMarkersFromData(pageResult?.data);
        });
      } catch (e) {
        console.log(e);
      }
    },

    /**
     * Fetch nearest resellers (all pages)
     */
    async fetchNearestResellers(place) {
      if (!place.geometry) {
        return false;
      }

      try {
        const location = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        };

        const baseParams = {
          lat: location.lat,
          lng: location.lng,
        };

        const firstResponse = await Api.get("", {
          params: { ...baseParams, page: 1, limit: 250 },
        });
        const result = firstResponse.data;

        // Support legacy format (direct array) for backwards compatibility
        const items = result.data ?? (Array.isArray(result) ? result : null);
        if (!items) {
          console.log(result);
          return false;
        }

        this.markers = [];
        this.addMarkersFromData(items);

        const pagination = result.pagination;
        if (pagination?.hasMore && pagination.pages > 1) {
          const limit = pagination.limit;
          const totalPages = pagination.pages;
          const otherPagePromises = [];
          for (let page = 2; page <= totalPages; page++) {
            otherPagePromises.push(
              Api.get("", {
                params: { ...baseParams, page, limit },
              }).then((res) => res.data),
            );
          }

          const otherResults = await Promise.all(otherPagePromises);
          otherResults.forEach((pageResult) => {
            const pageItems =
              pageResult?.data ??
              (Array.isArray(pageResult) ? pageResult : null);
            this.addMarkersFromData(pageItems);
          });
        }

        this.hasSearch = true;
        this.setCurrentPlace(place);
      } catch (e) {
        console.log(e);
      }
    },

    /**
     * Tobble infoWindow
     */
    toggleInfoWindow: function(marker, id) {
      this.infoWindowWindowPosition = marker.position;
      this.infoWindowContent = this.getInfoWindowContent(marker);

      if (this.selectedInfoWindow == id) {
        this.infoWindowOpen = !this.infoWindowOpen;
      } else {
        this.infoWindowOpen = true;
        this.selectedInfoWindow = id;
      }
    },

    /**
     * Get infoWindow content
     */
    getInfoWindowContent: function(marker) {
      return `
                    <div class="card card-resellers">
                        <div class="card-content">
                            <strong class="uppercase d-block mb-5">${
                              marker.data.name
                            }</strong>
                            <div>${marker.data.address}</div>
                            <div>${marker.data.zipcode} - ${
        marker.data.city
      }</div>
                            <div>${marker.data.country}</div>
                            <hr class="${
                              marker.data.phone ||
                              marker.data.email ||
                              marker.data.website
                                ? ""
                                : "d-none"
                            }">
                            <div class="${marker.data.phone ? "" : "d-none"}">
                                <svg aria-hidden="true" focusable="false" data-prefix="far" data-icon="phone" class="svg-inline--fa fa-phone fa-w-16" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill="currentColor" d="M476.5 22.9L382.3 1.2c-21.6-5-43.6 6.2-52.3 26.6l-43.5 101.5c-8 18.6-2.6 40.6 13.1 53.4l40 32.7C311 267.8 267.8 311 215.4 339.5l-32.7-40c-12.8-15.7-34.8-21.1-53.4-13.1L27.7 329.9c-20.4 8.7-31.5 30.7-26.6 52.3l21.7 94.2c4.8 20.9 23.2 35.5 44.6 35.5C312.3 512 512 313.7 512 67.5c0-21.4-14.6-39.8-35.5-44.6zM69.3 464l-20.9-90.7 98.2-42.1 55.7 68.1c98.8-46.4 150.6-98 197-197l-68.1-55.7 42.1-98.2L464 69.3C463 286.9 286.9 463 69.3 464z"></path></svg>
                                <a href="tel:${marker.data.phone}">${
        marker.data.phone
      }</a>
                            </div>
                            <div class="${marker.data.email ? "" : "d-none"}">
                                <svg aria-hidden="true" focusable="false" data-prefix="far" data-icon="envelope" class="svg-inline--fa fa-envelope fa-w-16" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill="currentColor" d="M464 64H48C21.49 64 0 85.49 0 112v288c0 26.51 21.49 48 48 48h416c26.51 0 48-21.49 48-48V112c0-26.51-21.49-48-48-48zm0 48v40.805c-22.422 18.259-58.168 46.651-134.587 106.49-16.841 13.247-50.201 45.072-73.413 44.701-23.208.375-56.579-31.459-73.413-44.701C106.18 199.465 70.425 171.067 48 152.805V112h416zM48 400V214.398c22.914 18.251 55.409 43.862 104.938 82.646 21.857 17.205 60.134 55.186 103.062 54.955 42.717.231 80.509-37.199 103.053-54.947 49.528-38.783 82.032-64.401 104.947-82.653V400H48z"></path></svg>
                                <a href="mailto:${marker.data.email}">${
        marker.data.email
      }</a>
                            </div>
                            <div class="${marker.data.website ? "" : "d-none"}">
                                <svg aria-hidden="true" focusable="false" data-prefix="far" data-icon="browser" class="svg-inline--fa fa-browser fa-w-16" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill="currentColor" d="M464 32H48C21.5 32 0 53.5 0 80v352c0 26.5 21.5 48 48 48h416c26.5 0 48-21.5 48-48V80c0-26.5-21.5-48-48-48zM48 92c0-6.6 5.4-12 12-12h40c6.6 0 12 5.4 12 12v24c0 6.6-5.4 12-12 12H60c-6.6 0-12-5.4-12-12V92zm416 334c0 3.3-2.7 6-6 6H54c-3.3 0-6-2.7-6-6V168h416v258zm0-310c0 6.6-5.4 12-12 12H172c-6.6 0-12-5.4-12-12V92c0-6.6 5.4-12 12-12h280c6.6 0 12 5.4 12 12v24z"></path></svg>
                                <a href="//${this.formatWebsiteUrl(
                                  marker.data.website,
                                )}" target="_blank">${this.formatWebsiteUrl(
        marker.data.website,
      )}</a>
                            </div>
                            <hr class="${
                              this.showBrands && this.getBrands(marker.data)
                                ? ""
                                : "d-none"
                            }">
                            <div style="max-width: 250px;" class="${
                              this.showBrands ? "" : "d-none"
                            }">
                                ${this.getBrands(marker.data)}
                            </div>
                        </div>
                    </div>
                `;
    },

    /**
     * Geolocate
     */
    geolocate: function() {
      navigator.geolocation.getCurrentPosition((position) => {
        const location = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        this.currentPlace = location;
        this.currentLocation = location;
        this.zoom = 12;
      });
    },

    /**
     * Center position
     */
    setCurrentPlace(place) {
      if (!place.geometry) {
        return false;
      }

      const location = {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      };

      this.currentPlace = location;
      this.currentLocation = location;
      this.zoom = 11;
    },

    /**
     * Format website url
     */
    formatWebsiteUrl(url) {
      var reg = /^(https?|http):\/\//;

      if (url) {
        return url.replace(reg, "");
      }

      return "";
    },

    /**
     * InitialPosition
     */
    async setInitialPostion() {
      const urlParams = new URLSearchParams(window.location.search);

      if (urlParams.has("location")) {
        this.$gmapApiPromiseLazy().then(() => {
          const geocoder = new this.google.maps.Geocoder();

          const address = urlParams.get("location");

          geocoder.geocode({ address }, (results, status) => {
            if (status === "OK") {
              const location = {
                lat: results[0].geometry.location.lat(),
                lng: results[0].geometry.location.lng(),
              };
              this.currentLocation = location;
            }
          });
        });
      }

      if (urlParams.has("zoom") && urlParams.get("zoom") !== "") {
        this.zoom = parseInt(urlParams.get("zoom"));
      }
    },

    /**
     * Has brands
     */
    getBrands(data) {
      if (!data?.brands || !Array.isArray(data.brands)) {
        return "";
      }

      const resellerBrands = data.brands.map((b) => String(b).toLowerCase());
      let brands = "";

      for (let i = 0; i < this.brands.length; i++) {
        const brandKey = this.brands[i].toLowerCase();
        if (resellerBrands.includes(brandKey)) {
          brands += `
                        <a href="#" class="mr-5 mt-5 inline-block">
                            <img src="${this.cdnUrl}/assets/img/brands/${this.brands[i]}.jpg" alt="${this.brands[i]}" height="20"/>
                        </a>
                    `;
        }
      }

      return brands;
    },
  },
  async mounted() {
    await this.setInitialPostion();

    await this.fetchAllResellers();

    this.geolocate();
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss">
body {
  margin: 0;
  padding: 0;
  height: 100%;
  width: 100%;
  font-family: "Roboto", sans-serif;
  font-size: 16px;
  text-rendering: optimizeLegibility;
  line-height: 1.5em;
  font-weight: 400;
  color: #000;

  a {
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  &.fiberdeck {
    font-family: "Open Sans", sans-serif;

    a {
      color: #96847a;
      font-weight: 400;

      &:visited {
        color: #96847a;
      }
    }

    #search-container {
      .form-control {
        background-color: transparent;
        border: 2px solid #000;

        &::-webkit-input-placeholder {
          /* Chrome/Opera/Safari */
          font-family: "Open Sans", sans-serif;
        }
        &::-moz-placeholder {
          /* Firefox 19+ */
          font-family: "Open Sans", sans-serif;
        }
        &:-ms-input-placeholder {
          /* IE 10+ */
          font-family: "Open Sans", sans-serif;
        }
        &:-moz-placeholder {
          /* Firefox 18- */
          font-family: "Open Sans", sans-serif;
        }
      }

      .input-group-text {
        color: #fff;
        background-color: #96847a;
        border: 1px solid #96847a;
      }
    }

    #results-container {
      .card-header {
        background-color: #dcd4d0;
      }
    }
  }

  &.felixwood {
    font-family: "Popins", sans-serif;

    a {
      color: #00a66c;
      font-weight: 400;
    }

    #search-container {
      .form-control {
        background-color: transparent;
        border: 2px solid #d7d7d7;

        &::-webkit-input-placeholder {
          /* Chrome/Opera/Safari */
          font-family: "Popins", sans-serif;
        }
        &::-moz-placeholder {
          /* Firefox 19+ */
          font-family: "Popins", sans-serif;
        }
        &:-ms-input-placeholder {
          /* IE 10+ */
          font-family: "Popins", sans-serif;
        }
        &:-moz-placeholder {
          /* Firefox 18- */
          font-family: "Popins", sans-serif;
        }
      }

      .input-group-text {
        color: #fff;
        background-color: #00a66c;
        border: 1px solid #00a66c;
      }
    }

    #results-container {
      .card-header {
        background-color: #03a66c;
        color: white;
      }
    }
  }

  &.cobrafastener {
    font-family: "Fira Sans", sans-serif;

    a {
      color: #dc0d17;
      font-weight: 400;
    }

    #search-container {
      .form-control {
        background-color: transparent;
        border: 2px solid #d7d7d7;

        &::-webkit-input-placeholder {
          /* Chrome/Opera/Safari */
          font-family: "Popins", sans-serif;
        }
        &::-moz-placeholder {
          /* Firefox 19+ */
          font-family: "Popins", sans-serif;
        }
        &:-ms-input-placeholder {
          /* IE 10+ */
          font-family: "Popins", sans-serif;
        }
        &:-moz-placeholder {
          /* Firefox 18- */
          font-family: "Popins", sans-serif;
        }
      }

      .input-group-text {
        color: #fff;
        background-color: #dc0d17;
        border: 1px solid #dc0d17;
      }
    }

    #results-container {
      .card-header {
        background-color: #dc0d17;
        color: white;
      }
    }
  }
}

#resellers-map {
  height: 100vh;
  width: 100%;
}

#search-container {
  position: absolute;
  background-color: #fff;
  box-shadow: 0 1px 10px rgba(0, 0, 0, 0.15);
  padding: 20px;
  margin: 0 auto;
  z-index: 1;
  width: 380px;
  right: 15%;
  top: 15%;

  @media (max-width: 600px) {
    top: 0;
    left: 0;
    right: 0;
    width: auto;
  }

  h3 {
    margin-top: 0;
    text-align: center;
  }

  .form-control {
    padding: 10px;
    background-color: transparent;
    border: 2px solid lightgray;
    outline: none;
    box-shadow: none;
    border-radius: 0;
  }

  .input-group-text {
    background: lightgray;
    border-color: lightgray;
    border-style: solid;
    color: #000;
    height: 100%;
    min-width: 45px;

    svg {
      width: 20px;
    }

    &:focus {
      outline: none;
    }
  }
}

#results-container {
  width: 300px;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  bottom: 0;
  height: 100%;
  background: #fff;
  overflow: hidden;

  .card-header {
    background-color: lightgray;
    position: absolute;
    top: 0;
    width: 100%;
    line-height: 50px;
    text-align: center;
    font-weight: 700;
    font-size: 18px;
  }

  .card-content {
    background-color: #fff;
    height: calc(100% - 50px);
    top: 50px;
    position: relative;
    overflow-y: scroll;
  }

  .list-group {
    display: -ms-flexbox;
    display: flex;
    -ms-flex-direction: column;
    flex-direction: column;
    padding-left: 0;
    margin-bottom: 0;
    margin-top: 0;

    .list-group-item {
      position: relative;
      display: block;
      padding: 0.75rem 1.25rem;
      background-color: #fff;
      border-bottom: 1px solid rgba(0, 0, 0, 0.125);
      padding: 10px;
      font-size: 14px;

      svg {
        display: inline;
        height: 12px;
        position: relative;
        top: 2px;
        margin-right: 4px;
      }

      &:hover {
        background: #efefef;
      }
    }

    &:last-child {
      border-bottom: 0;
    }
  }
}

.input-group {
  position: relative;
  display: -ms-flexbox;
  display: flex;
  -ms-flex-wrap: wrap;
  flex-wrap: wrap;
  -ms-flex-align: stretch;
  align-items: stretch;
  width: 100%;

  > .form-control {
    position: relative;
    -ms-flex: 1 1 auto;
    flex: 1 1 auto;
    width: 1%;
    min-width: 0;
    margin-bottom: 0;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;

    &::-webkit-input-placeholder {
      /* Chrome/Opera/Safari */
      font-family: "Roboto", sans-serif;
      font-style: italic;
    }
    &::-moz-placeholder {
      /* Firefox 19+ */
      font-family: "Roboto", sans-serif;
      font-style: italic;
    }
    &:-ms-input-placeholder {
      /* IE 10+ */
      font-family: "Roboto", sans-serif;
      font-style: italic;
    }
    &:-moz-placeholder {
      /* Firefox 18- */
      font-family: "Roboto", sans-serif;
      font-style: italic;
    }
  }
}

.card-resellers {
  padding: 5px;

  svg {
    display: inline;
    height: 12px;
    position: relative;
    top: 2px;
    margin-right: 4px;
  }

  hr {
    border-bottom: 0;
    border-color: lightgrey;
    border-style: solid;
  }
}

.uppercase {
  text-transform: uppercase;
}

.pac-container {
  margin-top: -2px;
  margin-left: 2px;
}

.text-muted {
  color: #6c757d;
}

.d-none {
  display: none !important;
}

.mr-5 {
  margin-right: 5px;
}

.mt-5 {
  margin-top: 5px;
}

.mb-5 {
  margin-bottom: 5px;
}

.d-block {
  display: block !important;
}

.inline-block {
  display: inline-block !important;
}
</style>
